import { type ReactNode, type ElementType, type HTMLAttributes } from "react";
import { cn } from "../../lib/cn";

/**
 * Other HTML attributes (role, aria-*, id, data-*) are forwarded to the
 * rendered element.
 */
export interface StackProps
  extends Omit<HTMLAttributes<HTMLElement>, "children" | "className"> {
  /** Stacked children. */
  children: ReactNode;
  /**
   * Stack axis. Defaults to `vertical`.
   * @default "vertical" 
   */
  direction?: "vertical" | "horizontal";
  /**
   * Gap step between items, mapped to Tailwind gap classes (xs 4px, sm 8px, md
   * 16px, lg 24px, xl 32px). Defaults to `md`.
   * @default "md" 
   */
  gap?: "none" | "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Cross-axis alignment. Defaults to `stretch`.
   * @default "stretch" 
   */
  align?: "stretch" | "start" | "center" | "end";
  /** Main-axis distribution. Defaults to `start`. */
  justify?: "start" | "center" | "end" | "between" | "around";
  /** Allow wrapping on horizontal stacks. Defaults to `false`. */
  wrap?: boolean;
  /** Stack class names. */
  className?: string;
  /**
   * Polymorphic element. "ul" and "ol" get role="list" (unless you pass a role)
   * so Safari/VoiceOver keep list semantics under list-style: none. Defaults to
   * `div`. Typed as any React ElementType, so the values list is every JSX
   * intrinsic; only container or list elements (div, section, nav, ul, ol) make
   * sense here.
   * @default "div"
   */
  as?: ElementType;
}

const gapClasses = {
  none: "gap-0",
  xs: "gap-1",      // 4px
  sm: "gap-2",      // 8px
  md: "gap-4",      // 16px
  lg: "gap-6",      // 24px
  xl: "gap-8",      // 32px
} as const;

const alignClasses = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
} as const;

const justifyClasses = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
} as const;

/**
 * Stack component.
 */
export function Stack({
  children,
  direction = "vertical",
  gap = "md",
  align = "stretch",
  justify = "start",
  wrap = false,
  className,
  as: Component = "div",
  role,
  ...rest
}: StackProps) {
  // Tailwind preflight sets list-style: none, which makes Safari/VoiceOver
  // drop list semantics; an explicit role="list" restores them.
  const isList = Component === "ul" || Component === "ol";
  return (
    <Component
      {...rest}
      role={role ?? (isList ? "list" : undefined)}
      className={cn(
        "flex",
        direction === "vertical" ? "flex-col" : "flex-row",
        gapClasses[gap],
        alignClasses[align],
        justifyClasses[justify],
        wrap && "flex-wrap",
        className
      )}
    >
      {children}
    </Component>
  );
}
